import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDatabase, 
  faBell, 
  faHome,
  faExclamationTriangle,
  faWifi,
  faExpand,
  faUserCircle,
  faCheck,
  faExclamationCircle,
  faTimes,
  faSignOutAlt,
  faChartLine,
  faQuestionCircle,
  faFileAlt
} from '@fortawesome/free-solid-svg-icons';
import stellapsLogo from "../../assets/Stellapps_logo_Horizontal.png";
import { provisioningService } from '../../services/provisioningServices';
import './Sitewrapper.scss';

const SiteWrapper = ({ children }) => {
  const [networkAlert, setNetworkAlert] = useState({
    show: false,
    type: 'danger',
    message: 'Network connection lost',
  });

  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [isOnline, setIsOnline] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [unreadAlertIds, setUnreadAlertIds] = useState(new Set());
  const [showAlertDropdown, setShowAlertDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const alertDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const plantName = "TEST PLANT ONE";
        const response = await provisioningService.getDashboardAlertsInfo(plantName);

        const alertsData =
          (response && response.content && response.content.alerts) ||
          (response && response.alerts) ||
          [];

        const safeAlerts = Array.isArray(alertsData) ? alertsData : [];
        
        setAlerts(prevAlerts => {
          const newAlerts = safeAlerts.filter(newAlert => 
            !prevAlerts.some(existingAlert => 
              JSON.stringify(existingAlert) === JSON.stringify(newAlert)
            )
          );
          
          if (newAlerts.length > 0) {
            setUnreadAlertIds(prev => {
              const newSet = new Set(prev);
              newAlerts.forEach(alert => {
                const alertId = JSON.stringify(alert);
                newSet.add(alertId);
              });
              return newSet;
            });
          }
          
          return safeAlerts;
        });
      } catch (error) {
        console.error("Error fetching alerts:", error);
        setAlerts([]);
      }
    };

    fetchAlerts(); 
    const interval = setInterval(fetchAlerts, 90000); 
    return () => clearInterval(interval); 
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setNetworkAlert({
        show: true,
        type: 'success',
        message: 'Connection restored',
      });
      setTimeout(() => setNetworkAlert(prev => ({ ...prev, show: false })), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setNetworkAlert({
        show: true,
        type: 'danger',
        message: 'Network connection lost',
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (alertDropdownRef.current && !alertDropdownRef.current.contains(event.target)) {
          if (!event.target.closest('.bell-icon-container button')) {
              setShowAlertDropdown(false);
          }
      }
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        if (!event.target.closest('.profile-button')) {
          setShowProfileDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  const toggleAlertDropdown = () => {
    setShowAlertDropdown(!showAlertDropdown);
    setShowProfileDropdown(false); 
  };

  const toggleProfileDropdown = () => {
    setShowProfileDropdown(!showProfileDropdown);
    setShowAlertDropdown(false); 
  };

  const markAlertsAsRead = () => {
    setUnreadAlertIds(new Set());
    setShowAlertDropdown(false);
  };

  const handleCloseDropdown = () => {
    setShowAlertDropdown(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userData");
    sessionStorage.clear();
    window.location.href = `${process.env.REACT_APP_LOGOUT_URL}`;
  };

  const parseAlertMessage = (alert) => {
    if (typeof alert !== 'string') {
      return {
        alert_name: 'Alert',
        time: new Date().toLocaleString()
      };
    }
    
    const lines = alert.split('\n');
    const parsed = {};
    lines.forEach(line => {
      const parts = line.split(':').map(part => part.trim());
      if (parts.length >= 2) {
        const key = parts[0].replace(/\s+/g, '_').toLowerCase();
        parsed[key] = parts.slice(1).join(':').trim();
      }
    });
    return parsed;
  };

  const navigationButtons = [
    { 
      icon: faHome, 
      label: 'Home', 
      path: '/',
      active: location.pathname === '/plant-monitoring/dashboard' || location.pathname === '/home'
    },
    { 
      icon: faExclamationCircle,
      label: 'Alerts', 
      path: '/plant-monitoring/monitoring/alerts/alertInfo',
      active: location.pathname.includes('/plant-monitoring/monitoring/alerts') || 
              location.pathname === '/alerts'
    },
    { 
      icon: faDatabase, 
      label: 'Data', 
      path: '/plant-monitoring/monitoring/devicedata',
      active: location.pathname.includes('/plant-monitoring/monitoring/devicedata') || 
              location.pathname === '/data'
    },
    {
  icon: faChartLine, 
  label: 'Graph',
  path: '/plant-monitoring/monitoring/graph',
  active: location.pathname.includes('/plant-monitoring/monitoring/graph') || 
          location.pathname === '/graph'
}
  ];

  const unreadCount = unreadAlertIds.size;

  return (
    <div className="site-wrapper">
      {networkAlert.show && (
        <div className="network-alert-container">
          <div className="network-alert-overlay" />
          <div className={`network-alert ${networkAlert.type === 'danger' ? '' : 'online'}`}>
            <FontAwesomeIcon 
              icon={networkAlert.type === 'danger' ? faExclamationTriangle : faWifi} 
              className="alert-icon" 
            />
            <span className="alert-message">{networkAlert.message}</span>
          </div>
        </div>
      )}

      <header className="dashboard-header">
        <div className="header-left">
          <img src={stellapsLogo} alt="Stellapps Logo" className="stellapsLogo-image" />
        </div>
{/* <div className="datetime-display">
            {currentDateTime.toLocaleString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            })}
          </div> */}
        <div className="header-center">
          <h1>MooMark MPP2 Bidadi</h1>
          <div className="datetime-display">
            {currentDateTime.toLocaleString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            })}
          </div>
        </div>

        <div className="header-right">
          <div className="nav-buttons">
            {navigationButtons.map((button, index) => (
              <button
                key={index}
                className={`nav-button ${button.active ? 'active' : ''}`}
                onClick={() => history.push(button.path)}
              >
                <FontAwesomeIcon icon={button.icon} />
                <span>{button.label}</span>
              </button>
            ))}
            
            <div className="bell-icon-container">
              <button
                className={`nav-button ${showAlertDropdown ? 'active' : ''}`}
                onClick={toggleAlertDropdown}
              >
                <FontAwesomeIcon icon={faBell} />
                {unreadCount > 0 && (
                  <span className="notification-badge">
                    {unreadCount}
                  </span>
                )}
              </button>
              
              {showAlertDropdown && (
                <div className="alert-dropdown" ref={alertDropdownRef}>
                  <div className="alert-dropdown-header">
                    <h3>Alerts ({alerts.length})</h3>
                    {unreadCount > 0 && (
                      <span className="unread-count">{unreadCount} unread</span>
                    )}
                  </div>
                  <div className="alert-list-container">
                    <div className="alert-list">
                      {alerts.length > 0 ? (
                        alerts.map((alert, index) => {
                          const parsed = parseAlertMessage(alert);
                          const alertId = JSON.stringify(alert);
                          const isUnread = unreadAlertIds.has(alertId);
                          
                          return (
                            <div key={index} className={`alert-item ${isUnread ? 'unread' : ''}`}>
                              <div className="alert-title">{parsed.alert_name || parsed.title || 'Alert'}</div>
                              {parsed.section_info && <div>Section: {parsed.section_info}</div>}
                              {parsed.system_info && <div>System: {parsed.system_info}</div>}
                              {parsed.device_info && <div>Device: {parsed.device_info}</div>}
                              {parsed.value && <div>Value: {parsed.value}</div>}
                              {parsed.time && <div className="alert-time">Time: {parsed.time}</div>}
                            </div>
                          );
                        })
                      ) : (
                        <div className="no-alerts">No Alerts Available</div>
                      )}
                    </div>
                  </div>
                  <div className="alert-dropdown-footer">
                    <button 
                      className="mark-all-read green-btn"
                      onClick={markAlertsAsRead}
                      disabled={unreadCount === 0}
                    >
                      <FontAwesomeIcon icon={faCheck} />
                      <span>Mark all as read</span>
                    </button>
                    <button 
                      className="close-dropdown"
                      onClick={handleCloseDropdown}
                      title="Close"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="header-actions">
              <button 
                className="action-button"
                onClick={toggleFullscreen}
                title="Toggle fullscreen"
              >
                <FontAwesomeIcon icon={faExpand} />
              </button>
              
              <div className="profile-section">
                <button 
                  className="profile-button" 
                  onClick={toggleProfileDropdown}
                >
                  <FontAwesomeIcon icon={faUserCircle} className="profile-icon" />
                </button>
                
                {showProfileDropdown && (
                  <div className="profile-dropdown" ref={profileDropdownRef}>
                    <div className="profile-dropdown-item profile-item">
                      <FontAwesomeIcon icon={faUserCircle} />
                      <span>Profile</span>
                    </div>
                    {/* FAQ item in profile dropdown */}
                    <div 
                      className="profile-dropdown-item profile-item faq-item"
                      onClick={() => {
                        history.push('/plant-monitoring/monitoring/faq');
                        setShowProfileDropdown(false);
                      }}
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} />
                      <span>FAQ</span>
                    </div>
                    <div 
                      className="profile-dropdown-item logout-item"
                      onClick={handleLogout}
                    >
                      <FontAwesomeIcon icon={faSignOutAlt} />
                      <span>Logout</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        {children}
      </div>
    </div>
  );
};

export default SiteWrapper;
